import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setMessage } from "./message";
import DimensionSevice from "services/dimension.service";
import StateService from "services/state.service";

export const getServiceDimensionValues = createAsyncThunk(
  "Dimension/getValues",
  async ({ search }, thunkAPI) => {
    try {
      const data = await DimensionSevice.getDimenionValues(search);
      return { data };
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage({ message: message, type: "error" }));
      return thunkAPI.rejectWithValue();
    }
  },
  {
    // `ifMissing: true` skips the request when the store already holds an entry for the
    // dimension key, loading or settled. The check reads the live store and `pending`
    // inserts the entry synchronously, so cells mounting in the same commit cannot fire
    // duplicates, and a dimension that resolved empty or failed is not re-asked.
    condition: ({ search, ifMissing }, { getState }) => {
      if (!ifMissing) return true;
      const dimensions = getState()?.dimensions?.dimensions ?? [];
      return !dimensions.some((d) => d.dimensionKey === search?.dimensionKey);
    },
  }
);

export const getStateSetDimensionValues = createAsyncThunk(
  "Dimension/getStateSetValues",
  async ({ search }, thunkAPI) => {
    try {
      const data = await StateService.getStateData(search);
      return { data, search };
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      thunkAPI.dispatch(setMessage({ message: message, type: "error" }));
      return thunkAPI.rejectWithValue();
    }
  }
);

const initialState = {
  dimensions: [],
  loading: false,
};

// Helper function to get dimension key
const getDimensionKey = (action) => {
  const dimensionKey = action.meta.arg.search?.dimensionKey;
  if (dimensionKey) return dimensionKey;

  // Fallback to old key format for backward compatibility
  const name = action.meta.arg.search?.name;
  const implementor = action.meta.arg.search?.implementor;
  const type = action.meta.arg.search?.type;
  const field = action.meta.arg.search?.field;

  return `${name || ''}-${type || ''}-${implementor || ''}-${field || ''}`;
};

const DimensionSlice = createSlice({
  name: "Dimensions",
  initialState,

  extraReducers: {
    //Column Reducers

    [getServiceDimensionValues.pending]: (state, action) => {
      const dimensionKey = getDimensionKey(action);
      const name = action.meta.arg.search?.name;
      const implementor = action.meta.arg.search?.implementor;
      const type = action.meta.arg.search?.type;
      const field = action.meta.arg.search?.field;

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update loading to true
        state.dimensions[index].loading = true;
      } else {
        // If no match found, insert a new record with loading set to true
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values: [],
          loading: true,
        });
      }
    },
    [getServiceDimensionValues.fulfilled]: (state, action) => {
      const dimensionKey = getDimensionKey(action);
      const name = action.meta.arg.search?.name;
      const implementor = action.meta.arg.search?.implementor;
      const type = action.meta.arg.search?.type;
      const field = action.meta.arg.search?.field;

      const values = action.payload?.data?.lookUpLabels;

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update values and set loading to false
        state.dimensions[index].values = values;
        state.dimensions[index].loading = false;
      } else {
        // If no match found, insert a new record with loading set to false
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values,
          loading: false,
        });
      }
    },
    [getServiceDimensionValues.rejected]: (state, action) => {
      const dimensionKey = getDimensionKey(action);
      const name = action.meta.arg.search?.name;
      const implementor = action.meta.arg.search?.implementor;
      const type = action.meta.arg.search?.type;
      const field = action.meta.arg.search?.field;

      const values = []

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update values to empty and set loading to false
        state.dimensions[index].values = values;
        state.dimensions[index].loading = false;
      } else {
        // If no match found, insert a new record with empty values and loading set to false
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values,
          loading: false,
        });
      }
    },

    // State Set dimension values reducers
    [getStateSetDimensionValues.pending]: (state, action) => {
      const stateSetId = action.meta.arg.search?.stateSetId || "Unknown";
      const dimensionKey = `stateSet-${stateSetId}-id`;
      const name = stateSetId;
      const implementor = stateSetId;
      const type = "stateSet";
      const field = "id";

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update loading to true
        state.dimensions[index].loading = true;
      } else {
        // If no match found, insert a new record with loading set to true
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values: [],
          loading: true,
        });
      }
    },
    [getStateSetDimensionValues.fulfilled]: (state, action) => {
      const stateSetId = action.payload?.search?.stateSetId || "Unknown";
      const dimensionKey = `stateSet-${stateSetId}-id`;
      const name = stateSetId;
      const implementor = stateSetId;
      const type = "stateSet";
      const field = "id";

      // Transform the state set data to dimension values format
      const items = action.payload?.data?.list || [];
      let values;

      // Special handling for Contributor state set
      if (stateSetId === "Contributor") {
        values = items.map(contributor => ({
          value: contributor.id,
          label: contributor.name || "",
          description: `${contributor.email || ""}${contributor.email && contributor.mobile_phone ? " | " : ""}${contributor.mobile_phone || ""}`,
          roleName: contributor.role?.name || "",
          orgUnitId: contributor.department?.organization || ""
        }));
      } else {
        // Generic transformation for other state sets
        values = items.map(item => ({
          value: item.id,
          label: item.name || item.id || ""
        }));
      }

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update values and set loading to false
        state.dimensions[index].values = values;
        state.dimensions[index].loading = false;
      } else {
        // If no match found, insert a new record with loading set to false
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values,
          loading: false,
        });
      }
    },
    [getStateSetDimensionValues.rejected]: (state, action) => {
      const stateSetId = action.meta.arg.search?.stateSetId || "Unknown";
      const dimensionKey = `stateSet-${stateSetId}-id`;
      const name = stateSetId;
      const implementor = stateSetId;
      const type = "stateSet";
      const field = "id";
      const values = [];

      // Find the index of the record using dimensionKey
      const index = state.dimensions.findIndex(
        (item) => item.dimensionKey === dimensionKey
      );

      if (index !== -1) {
        // If record found, update values to empty and set loading to false
        state.dimensions[index].values = values;
        state.dimensions[index].loading = false;
      } else {
        // If no match found, insert a new record with empty values and loading set to false
        state.dimensions.push({
          dimensionKey,
          name,
          type,
          implementor,
          field,
          values,
          loading: false,
        });
      }
    },
  },
});

const { reducer, actions } = DimensionSlice;

export const { setDimensionRenderState } = actions;

export default reducer;
